import {
  Feature,
  FeatureCollection,
  MultiPolygon,
  Polygon,
  Position,
} from "geojson";
import mapboxgl, { GeoJSONFeature } from "mapbox-gl";
import tinygradient from "tinygradient";
import { LocationCoordinates } from "../../cra/models/CraNode";
import { calculateDistance } from "../../cra/utils/node-utils";
import turf from "../../shared/utils/turf";

// all styles at https://docs.mapbox.com/api/maps/styles/
export enum MapboxStylesUrl {
  Standard = "mapbox://styles/mapbox/streets-v12",
  Satellite = "mapbox://styles/mapbox/satellite-streets-v12",
}

export const defaultStrokeColor = "#666666";
export const notCodedColor = "#dedede";
export const mapGradientStart = "rgb(170, 255, 33)";
export const mapGradientEnd = "rgb(255, 44, 244)";
export const mapGradient = tinygradient([mapGradientStart, mapGradientEnd]);
export const mapGradientStep = (nSteps: number, stepIndex: number) =>
  `#${mapGradient.hsv(nSteps, true)[stepIndex].toHex()}`;

export const mapActive = (map?: mapboxgl.Map) => !!map && !map._removed;

/**
 * GeoJSONSource hides a few useful properties, such as _data.
 * Add more properties if you need them.
 */
export type CustomGeoSource = mapboxgl.GeoJSONSource & {
  _data: FeatureCollection;
};

export type CustomMouseEvent = mapboxgl.MapMouseEvent & {
  features?: mapboxgl.GeoJSONFeature[] | undefined;
};

interface MapCenteringProps {
  reducedMapSize?: boolean;
}
/**
 * Applies geoJson to the map and centers it around the whole way
 * or the selected node, if available
 */
function centerMap(
  map: mapboxgl.Map,
  extendBounds: (bounds: mapboxgl.LngLatBounds) => void,
  options: MapCenteringProps = {},
) {
  // TODO get the size of the map straight from the map instead of using "reducedMapSize"

  const bounds = new mapboxgl.LngLatBounds();

  extendBounds(bounds);

  if (!bounds.isEmpty()) {
    const smallScreenMultiplier = options.reducedMapSize ? 4 : 10;
    const nodeFocusedMultiplier = 20;
    try {
      map.fitBounds(bounds, {
        padding: smallScreenMultiplier * nodeFocusedMultiplier,
        duration: 1000,
        maxZoom: 18,
      });
    } catch (error) {
      // we always get an Invalid LngLat error on small screens
      // we don't need to fix it, but we should catch it to make sure it doesn't crash the app
      console.error(error);
    }
  }
}

const defaultConfig: mapboxgl.MapOptions = {
  container: "map",
  style: MapboxStylesUrl.Standard,
  center: [5, 43],
  zoom: 3,
};

// Used to decide whether a mouse click was a drag event
export const numbersAreNear = (
  n1: number,
  n2: number,
  distanceAllowed: number = 0.000025,
): boolean => Math.abs(n1 - n2) < distanceAllowed;

export const mapUtils = {
  centerMap,
  defaultConfig,
};

export const mergeFeatureCollections = (
  geoJsonList: FeatureCollection[],
): FeatureCollection => ({
  type: "FeatureCollection",
  features: geoJsonList.flatMap((geoJson) => geoJson.features),
});

export const totalLineStringMeters = (geoJson: FeatureCollection): number => {
  const toLocation = (location: Position): LocationCoordinates => ({
    latitude: location[0],
    longitude: location[1],
    altitude: 0,
  });

  var totalDistance = 0;
  geoJson.features.forEach((feature) => {
    if (feature.geometry.type === "LineString") {
      const coords = feature.geometry.coordinates;

      for (let i = 0; i < coords.length - 1; i++) {
        totalDistance += calculateDistance(
          toLocation(coords[i]),
          toLocation(coords[i + 1]),
        );
      }
    }
  });

  return totalDistance;
};

export function calculateGeographicHeading(
  [longitude1, latitude1]: number[],
  [longitude2, latitude2]: number[],
) {
  const lat1 = (latitude1 * Math.PI) / 180;
  const lat2 = (latitude2 * Math.PI) / 180;
  const dLon = ((longitude2 - longitude1) * Math.PI) / 180;

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  let heading = Math.atan2(y, x) * (180 / Math.PI);
  return (heading + 360) % 360; // Normalize to 0-360
}

export function polygonArea(coordinates: number[][]) {
  // coordinates: array of [longitude, latitude] pairs
  // Returns area in square meters

  const EARTH_RADIUS = 6371000; // meters

  function toRadians(degrees: number) {
    return (degrees * Math.PI) / 180;
  }

  if (coordinates.length < 3) return 0;

  let total = 0;

  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length;

    const lon1 = toRadians(coordinates[i][0]);
    const lat1 = toRadians(coordinates[i][1]);
    const lon2 = toRadians(coordinates[j][0]);
    const lat2 = toRadians(coordinates[j][1]);

    total += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }

  const area = Math.abs((total * EARTH_RADIUS * EARTH_RADIUS) / 2);
  return area;
}

export function isFeatureWithinPolygon(
  feature: GeoJSONFeature,
  bufferPolygon: Feature<Polygon | MultiPolygon>,
): boolean {
  const { type } = feature.geometry;

  if (type === "Point") {
    return turf.booleanPointInPolygon(feature.geometry, bufferPolygon);
  }

  if (type === "LineString" || type === "MultiLineString") {
    return turf.booleanIntersects(feature, bufferPolygon);
  }

  if (type === "Polygon") {
    return turf.booleanIntersects(feature, bufferPolygon);
  }

  return false;
}

export function addCustomIcon(
  map: mapboxgl.Map,
  name: string,
  svg: string,
  size: number,
  sdf: boolean = false,
) {
  const img = new Image(size, size);
  img.onload = () => {
    map.addImage(name, img, { sdf });
  };
  img.src = svg;
}

export function splitLineAtDistances(
  line: Feature<GeoJSON.LineString>,
  breakDistances: number[],
): Feature<GeoJSON.LineString>[] {
  const totalLength = turf.length(line, { units: "meters" });

  // Sort and clamp distances, bookend with 0 and total length
  const distances = [
    0,
    ...breakDistances
      .filter((d) => d > 0 && d < totalLength)
      .sort((a, b) => a - b),
    totalLength,
  ];

  // Convert distances to points along the line
  const breakpoints = distances.map((d) =>
    turf.along(line, d, { units: "meters" }),
  );

  // Slice between consecutive breakpoints
  const segments = [];
  for (let i = 0; i < breakpoints.length - 1; i++) {
    const segment = turf.lineSlice(breakpoints[i], breakpoints[i + 1], line);
    segments.push(segment);
  }

  return segments;
}
